import { useCallback, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { editSceneText, type EditSceneTextResponse } from './api';

/**
 * Screen-3 per-page text edit mutation hook.
 *
 * Fires `POST /api/book/orders/:orderId/scenes/:sceneId/text` on
 * blur when the customer changes a page text. Server enforces the
 * length corridor (G7); this hook does NOT gate the request client-
 * side. If the server rejects (`scene_text_too_short` /
 * `scene_text_too_long`), the error is exposed and the caller
 * decides whether to revert the local text or leave it in place.
 *
 * Same result-shape discipline as `use-anchor-accept` /
 * `use-anchor-regen`: `save()` returns
 * `{ ok: true; response } | { ok: false; error }` so the caller can
 * branch on the error's `error_code` inline.
 *
 * Per-page saves do NOT trigger a full envelope refetch. The Weiter
 * button (leave-text-editing) is the transition point; only that
 * mutation drives the refetch that lets BookRoute re-dispatch.
 */

export type EditSceneTextResult =
  | { readonly ok: true; readonly response: EditSceneTextResponse }
  | { readonly ok: false; readonly error: ApiError | null };

export interface UseSceneTextEditState {
  readonly saving: boolean;
  readonly error: ApiError | null;
  readonly save: (sceneId: string, text: string) => Promise<EditSceneTextResult>;
}

export function useSceneTextEdit(orderId: string): UseSceneTextEditState {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const save = useCallback(
    async (sceneId: string, text: string): Promise<EditSceneTextResult> => {
      setSaving(true);
      setError(null);
      try {
        const response = await editSceneText(orderId, sceneId, text);
        return { ok: true, response };
      } catch (err) {
        const apiError = err instanceof ApiError ? err : null;
        setError(apiError);
        return { ok: false, error: apiError };
      } finally {
        setSaving(false);
      }
    },
    [orderId],
  );

  return { saving, error, save };
}
